import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { createNumberColumn, createTextColumn } from '@ipfabric/utils';
import { AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS } from '@ipfabric/utils/aim/flow-protocols';
import { unsafeAssert } from '@ipfabric/utils/types/unsafeAssert';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableBulkAction } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type {
  AimApplicationDraft,
  AimFlowDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';
import { AimDataTransferManager } from '../dataTransfer/AimDataTransferManager';
import { AimFlowDialog } from '../dialogs/AimFlowDialog';
import { AimInvalidBanner } from './helpers/AimInvalidBanner';
import { useAimOrphanDetection } from './helpers/useAimOrphanDetection';
import { useAimRowActions } from './helpers/useAimRowActions';

type Props = {
  form: UseFormReturn<{
    aimFlows: AimFlowDraft[];
    aimWorkloads: AimWorkloadDraft[];
    aimApplications: AimApplicationDraft[];
  }>;
};

type FlowRow = AimFlowDraft & {
  srcWorkloadName: string;
  srcWorkloadOrphan: boolean;
  dstWorkloadName: string;
  dstWorkloadOrphan: boolean;
};

const renderRefCell = (label: string, orphan: boolean, rawId: string) =>
  orphan ? (
    <Tooltip content={`References missing entity: ${rawId}`}>
      <span className='inline-flex items-center gap-xs text-warning'>
        <Icon glyph='fa-exclamation-triangle' size='sm' />
        {label}
      </span>
    </Tooltip>
  ) : (
    label
  );

export const AimFlowsTable = ({ form }: Props) => {
  const flows = useWatch({ control: form.control, name: 'aimFlows' });
  const workloads = useWatch({
    control: form.control,
    name: 'aimWorkloads',
  });

  const orphanDetection = useAimOrphanDetection(form as never);
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);
  const [importExportOpen, setImportExportOpen] = useState(false);

  const actions = useAimRowActions<AimFlowDraft>({
    fieldName: 'aimFlows' as never,
    control: form.control as never,
    setValue: form.setValue as never,
    isReferenced: () => [], // Flow is a leaf entity — nothing references flows
    deleteConfirmLabel: (count) => ({
      header: `${count} ${plural(count, 'flow')} will be deleted`,
      subHeader:
        count === 1
          ? 'Do you really want to delete this flow?'
          : `Do you really want to delete ${count} selected flows?`,
      btnConfirm: 'Delete',
      btnSecondaryConfirm: 'Cancel',
    }),
    blockedDeleteLabel: () => ({
      header: 'Internal error: flow blocked from delete',
      btnConfirm: 'OK',
    }),
  });

  const tableData = useMemo<FlowRow[]>(() => {
    const wlById = new Map(workloads.map((w) => [w.id, w]));
    return flows.map((f) => ({
      ...f,
      srcWorkloadName: wlById.get(f.srcWorkloadId)?.name ?? f.srcWorkloadId,
      srcWorkloadOrphan: !wlById.has(f.srcWorkloadId),
      dstWorkloadName: wlById.get(f.dstWorkloadId)?.name ?? f.dstWorkloadId,
      dstWorkloadOrphan: !wlById.has(f.dstWorkloadId),
    }));
  }, [flows, workloads]);

  const displayedData = useMemo(
    () =>
      showOnlyInvalid
        ? tableData.filter((row) => orphanDetection.flowOrphanIds.has(row.id))
        : tableData,
    [tableData, showOnlyInvalid, orphanDetection.flowOrphanIds],
  );

  const columns = useMemo(
    () => [
      createTextColumn<FlowRow>({
        key: 'srcWorkloadName',
        title: 'Source workload',
        renderContent: ({ row }: { row: FlowRow }) => ({
          content: renderRefCell(
            row.srcWorkloadName,
            row.srcWorkloadOrphan,
            row.srcWorkloadId,
          ),
        }),
      }),
      createTextColumn<FlowRow>({
        key: 'dstWorkloadName',
        title: 'Destination workload',
        renderContent: ({ row }: { row: FlowRow }) => ({
          content: renderRefCell(
            row.dstWorkloadName,
            row.dstWorkloadOrphan,
            row.dstWorkloadId,
          ),
        }),
      }),
      createTextColumn<FlowRow>({
        key: 'srcIp',
        title: 'Source IP',
      }),
      createTextColumn<FlowRow>({
        key: 'dstIp',
        title: 'Destination IP',
      }),
      createNumberColumn<FlowRow>({
        key: 'protocol',
        title: 'Protocol',
        renderContent: ({ row }: { row: FlowRow }) => {
          return {
            content:
              AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS[
                row.protocol.toString() as keyof typeof AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS
              ] ?? row.protocol,
          };
        },
      }),
      createNumberColumn<FlowRow>({
        key: 'srcPort',
        title: 'Source port',
      }),
      createNumberColumn<FlowRow>({
        key: 'dstPort',
        title: 'Destination port',
      }),
      createFrontendOnlyColumn<FlowRow>({
        key: 'actions',
        disabledSort: true,
        title: 'Actions',
        render: (row) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              dataTestId={`aim-flows-${row.srcWorkloadName}-${row.dstWorkloadName}-edit-icon`}
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={() => actions.handleEdit(row)}
              type='button'
            />
            <TableButton
              dataTestId={`aim-flows-${row.srcWorkloadName}-${row.dstWorkloadName}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={() => actions.handleDelete(row.id)}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [actions],
  );

  const bulkActions: TableBulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        title: 'Delete',
        accent: 'negative',
        icon: 'fa-trash-can',
        disableAutoDeselect: true,
        onClick: async ({ selectedRows, deselectAll }) => {
          const ok = await actions.handleBulkDelete(
            unsafeAssert<Set<unknown>, Set<string>>(selectedRows),
          );
          if (ok) {
            deselectAll();
          }
        },
      },
    ],
    [actions],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: displayedData,
  });

  return (
    <Card className='flex flex-col flex-1 rounded-tl-none' noShadow>
      <AimInvalidBanner
        invalidCount={orphanDetection.flowOrphanIds.size}
        showOnlyInvalid={showOnlyInvalid}
        onToggle={() => setShowOnlyInvalid(!showOnlyInvalid)}
        invalidLabel='flows have unresolved Workload references'
      />
      <Table
        borderless
        bulkActions={bulkActions}
        rowIdField='id'
        loadRowIdsForSelectAll='fromData'
        className='flex-1 bg-transparent'
        columns={columns}
        rowHeight={44}
        title='Flow list'
        menuItems={[
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='add-new'
                dataTestId='aim-flows-add'
                variant='outline'
                size='small'
                icon='fa-plus-circle'
                onClick={actions.handleAdd}
              >
                Add new
              </Button>
            ),
          },
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='import-export'
                variant='outline'
                size='small'
                icon='fa-file-import'
                onClick={() => setImportExportOpen(true)}
              >
                Import data
              </Button>
            ),
          },
        ]}
        {...frontendSimpleFilter}
      />
      {actions.editedItem !== null && (
        <AimFlowDialog
          formType={actions.editedItem === 'new' ? 'add' : 'edit'}
          initialValues={
            actions.editedItem === 'new' ? undefined : actions.editedItem
          }
          availableWorkloads={workloads}
          onClose={actions.handleCloseDialog}
          onSubmit={actions.handleSubmitDialog}
        />
      )}
      {actions.confirmDeleteDialogRenderer()}
      {importExportOpen && (
        <AimDataTransferManager
          triggeredFrom='flows'
          form={form as never}
          onClose={() => setImportExportOpen(false)}
        />
      )}
    </Card>
  );
};
